import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { getAuthToken } from '../utils/auth';
import { getAvatarUrl } from '../utils/avatar';

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  maxHeight: 200,
  overflow: 'auto',
  zIndex: 1000,
  marginTop: 4,
}));

const UserSearchDropdown = ({ 
  placeholder = "Search users...", 
  onUserSelect, 
  searchEndpoint,
  value = '',
  onChange,
  disabled = false,
  groupId = null,
  searchType = 'general' // 'general', 'group-members', 'group-invite'
}) => {
  const [query, setQuery] = useState(value);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState('');
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update internal query when value prop changes
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Search function with debouncing
  const searchUsers = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setUsers([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      let endpoint;
      const token = getAuthToken();
      
      // Determine which endpoint to use based on searchType
      switch (searchType) {
        case 'group-members':
          endpoint = `/groups/${groupId}/members/search`;
          break;
        case 'group-invite':
          endpoint = `/groups/${groupId}/search-users`;
          break;
        default:
          endpoint = '/users/search';
      }

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
        params: { q: searchQuery },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      // Handle different response formats
      const userData = response.data.users || response.data.members || [];
      setUsers(userData);
      setShowDropdown(true);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    
    if (onChange) {
      onChange(newQuery);
    }

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      searchUsers(newQuery);
    }, 300);
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setQuery(user.username);
    setShowDropdown(false);
    setUsers([]);
    
    if (onUserSelect) {
      onUserSelect(user);
    }

    if (onChange) {
      onChange(user.username);
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (query && users.length > 0) {
      setShowDropdown(true);
    }
  };

  return (
    <Box position="relative" ref={dropdownRef} width="100%">
      <TextField
        ref={inputRef}
        fullWidth
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        variant="outlined"
        size="medium"
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: 3,
            transition: 'all 0.25s ease-in-out',
            boxShadow: '0 4px 16px -8px rgb(59 130 246 / 0.28)',
            minHeight: 44,
            '@media (hover: hover)': {
              '&:hover': {
                boxShadow: '0 8px 24px -10px rgb(59 130 246 / 0.35)',
                transform: 'translateY(-1px)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#60a5fa',
                  borderWidth: 2,
                },
              },
            },
            '&.Mui-focused': {
              boxShadow: '0 16px 36px -12px rgb(59 130 246 / 0.45)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3b82f6',
                borderWidth: 2,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e2e8f0',
              borderWidth: 1,
            },
          },
          '& .MuiInputBase-input': {
            padding: '14px 16px',
            fontSize: '0.98rem',
            color: '#0f172a',
            fontWeight: 500,
            '&::placeholder': {
              color: '#64748b',
              opacity: 1,
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 1.25 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14Zm9.707 14.293-3.387-3.387a8.999 8.999 0 1 0-1.414 1.414l3.387 3.387a1 1 0 0 0 1.414-1.414Z" fill="#3b82f6"/>
              </svg>
            </Box>
          ),
          endAdornment: loading && (
            <CircularProgress 
              size={20} 
              sx={{ 
                color: '#3b82f6',
                mr: 1 
              }} 
            />
          )
        }}
      />
      
      {showDropdown && (
        <StyledPaper>
          {error && (
            <Box p={2}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}
          
          {!error && users.length === 0 && !loading && query && (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary">
                No users found
              </Typography>
            </Box>
          )}
          
          {!error && users.length > 0 && (
            <List dense>
              {users.map((user) => (
                <ListItem
                  key={user.id}
                  button
                  onClick={() => handleUserSelect(user)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={getAvatarUrl(user)}
                      alt={user.username}
                      sx={{ width: 32, height: 32 }}
                    >
                      {user.username?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.username}
                    secondary={user.email}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </StyledPaper>
      )}
    </Box>
  );
};

export default UserSearchDropdown;